import React, { useState, useEffect } from 'react';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import BaseModal from '../../../../../components/modals/BaseModal';
import { Button, Grid, TextField } from '@mui/material';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import dayjs from 'dayjs';
import { updateTimeClockEntry } from '../../../../../repository/TimeClockDAO';
import { ToastType, useToast } from '../../../../../context/toast/ToastContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import { useOrganizationContext } from '../../../../../context/organization_context/OrganizationContext';
import { Timestamp } from 'firebase/firestore';

const EditTimeClockModal = ({ show, onClose, docId, timeClockEntry, entryIndex, dayOfWeek }) => {
    const [entryTimeIn, setEntryTimeIn] = useState(null);
    const [entryTimeOut, setEntryTimeOut] = useState(null);
    const [reason, setReason] = useState("");
    const [timeInError, setTimeInError] = useState("");
    const [timeOutError, setTimeOutError] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const { firebaseUser } = useUserContext();
    const { organization } = useOrganizationContext();
    const { t } = useTranslation();

    useEffect(() => {
        if (show && timeClockEntry) {
            const timestampIn = timeClockEntry.timestamp_in;
            if (timestampIn) {
                const dayjsTimestampIn = dayjs(timestampIn.seconds * 1000 + timestampIn.nanoseconds / 1000000);
                setEntryTimeIn(dayjsTimestampIn);
            }

            const timestampOut = timeClockEntry.timestamp_out;
            if (timestampOut) {
                const dayjsTimestampOut = dayjs(timestampOut.seconds * 1000 + timestampOut.nanoseconds / 1000000);
                setEntryTimeOut(dayjsTimestampOut);
            }

            setReason('');
            setTimeInError('');
            setTimeOutError('');
        }
    }, [show, timeClockEntry]); 

    const validateTimes = () => {
        setTimeInError('');
        setTimeOutError('');

        if (!entryTimeIn) {
            setTimeInError(t(StringKeys.CLOCK_IN_TIME_REQUIRED));
            return false;
        }

        if (entryTimeOut) {
            if (entryTimeOut.isBefore(entryTimeIn)) {
                setTimeOutError(t(StringKeys.CLOCK_OUT_MUST_BE_AFTER_CLOCK_IN));
                return false;
            }

            // Optional: Add validation for maximum duration (e.g., 24 hours)
            const duration = entryTimeOut.diff(entryTimeIn, 'hours');
            if (duration > 24) {
                setTimeOutError(t(StringKeys.SHIFT_DURATION_EXCEEDS_24_HOURS));
                return false;
            }
        }

        return true;
    };

    const handleTimeInChange = (newValue) => {
        setEntryTimeIn(newValue);
        if (entryTimeOut && newValue && entryTimeOut.isBefore(newValue)) {
            setTimeOutError(t(StringKeys.CLOCK_OUT_MUST_BE_AFTER_CLOCK_IN));
        } else {
            setTimeOutError('');
        }
    };

    const handleTimeOutChange = (newValue) => {
        setEntryTimeOut(newValue);
        if (newValue && entryTimeIn && newValue.isBefore(entryTimeIn)) {
            setTimeOutError(t(StringKeys.CLOCK_OUT_MUST_BE_AFTER_CLOCK_IN));
        } else {
            setTimeOutError('');
        }
    };

    const handleSubmit = async () => {
        if (!validateTimes()) {
            showToast(t(StringKeys.CLOCK_OUT_MUST_BE_AFTER_CLOCK_IN), ToastType.DANGER);
            return;
        }

        if (!reason.trim()) {
            showToast(t(StringKeys.REASON_REQUIRED), ToastType.DANGER);
            return;
        }

        setLoadingData(true);

        const jsEntryDateIn = entryTimeIn ? entryTimeIn.toDate() : null;
        const jsEntryDateOut = entryTimeOut ? entryTimeOut.toDate() : null;

        if (!jsEntryDateIn || isNaN(jsEntryDateIn.getTime()) || (entryTimeOut && isNaN(jsEntryDateOut.getTime()))) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            setLoadingData(false);
            return;
        }

        try {
            const updates = {
                timestamp_in: Timestamp.fromDate(jsEntryDateIn)
            };

            if (jsEntryDateOut) {
                updates.timestamp_out = Timestamp.fromDate(jsEntryDateOut);
            }

            const result = await updateTimeClockEntry(
                organization.id,
                docId,
                firebaseUser.uid,
                updates,
                reason
            );

            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                onClose();
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    return (
        <BaseModal
            open={show}
            onClose={() => {
                onClose();
            }}
            maxWidth="sm"
            title={t(StringKeys.EDIT_TIME_CLOCK_ENTRY)}
            actions={<Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label={t(StringKeys.TIME_IN)}
                            value={entryTimeIn}
                            onChange={handleTimeInChange}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params}
                                    error={!!timeInError}
                                    helperText={timeInError}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label={t(StringKeys.TIME_OUT)}
                            value={entryTimeOut}
                            onChange={handleTimeOutChange}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <TextField 
                                    {...params}
                                    error={!!timeOutError}
                                    helperText={timeOutError}
                                    fullWidth={true}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label={t(StringKeys.REASON_FOR_EDIT)}
                            multiline
                            rows={4}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </BaseModal>
    );
};

export default EditTimeClockModal;
